import React from 'react';
import type { ComponentData, PageComponentItem, FlatComponentItem } from '../../types/contentstack';
import { WelcomeFlowTracking } from '../../services/analytics';

import { CmsHeroBannerCarousel } from '../CmsHeroBannerCarousel/CmsHeroBannerCarousel';
import CmsContentAccordion from '../CmsContentAccordion/CmsContentAccordion';
import CmsFeatures from '../CmsFeatures/CmsFeatures';
import CmsShoppingLinks from '../CmsShoppingLinks/CmsShoppingLinks';
import CmsJoinSection from '../CmsJoinSection/CmsJoinSection';
import CmsHomePageTimeline from '../CmsHomePageTimeline/CmsHomePageTimeline';
import CmsIconsAndCaption from '../CmsIconsAndCaption/CmsIconsAndCaption';
import CmsRichText from '../CmsRichText/CmsRichText';
import CmsRichTextLightBlue from '../CmsRichTextLightBlue/CmsRichTextLightBlue';
import CmsNoSearchResultsLinks from '../CmsNoSearchResultsLinks/CmsNoSearchResultsLinks';
import CmsPLPBanner from '../CmsPLPBanner/CmsPLPBanner';
import CmsPageHeader from '../CmsPageHeader/CmsPageHeader';
import CmsSkinnyRichText from '../CmsSkinnyRichText/CmsSkinnyRichText';
import CmsSkinnyRichTextLightBlue from '../CmsSkinnyRichTextLightBlue/CmsSkinnyRichTextLightBlue';
import CmsPrescriptionBlockBlue from '../CmsPrescriptionBlockBlue/CmsPrescriptionBlockBlue';
import CmsHomePageCallouts from '../CmsHomePageCallouts/CmsHomePageCallouts';
import CmsSignUpToday from '../CmsSignUpToday/CmsSignUpToday';
import CmsErrorHero from '../CmsErrorHero/CmsErrorHero';
import CmsHpsfHeroBanner from '../CmsHpsfHeroBanner/CmsHpsfHeroBanner';
import CmsSpecialPricingText from '../CmsSpecialPricingText/CmsSpecialPricingText';
import CmsShoppingLinksBlue from '../CmsShoppingLinksBlue/CmsShoppingLinksBlue';
import CmsNoSearchResultsSuggestions from '../CmsNoSearchResultsSuggestions/CmsNoSearchResultsSuggestions';
import CmsDigitalToolkit from '../CmsDigitalToolkit/CmsDigitalToolkit';

interface CmsComponentRendererProps {
  components: ComponentData[];
  onClose?: () => void;
  welcomeFlowTracking?: WelcomeFlowTracking;
}

const componentMap: Record<string, React.ElementType> = {
  CmsHeroBannerCarousel: CmsHeroBannerCarousel,
  CmsContentAccordion: CmsContentAccordion,
  AccordionsContentAccordion: CmsContentAccordion,
  CmsFeatures: CmsFeatures,
  CmsShoppingLinks: CmsShoppingLinks,
  CmsJoinSection: CmsJoinSection,
  CmsHomePageTimeline: CmsHomePageTimeline,
  CmsColumnImageText: CmsFeatures,
  CmsIconsAndCaption: CmsIconsAndCaption,
  CmsRichText: CmsRichText,
  CmsRichTextLightBlue: CmsRichTextLightBlue,
  CmsNoSearchResultsLinks: CmsNoSearchResultsLinks,
  CmsPLPBanner: CmsPLPBanner,
  CmsPageHeader: CmsPageHeader,
  CmsSkinnyRichText: CmsSkinnyRichText,
  CmsSkinnyRichTextLightBlue: CmsSkinnyRichTextLightBlue,
  CmsPrescriptionBlockBlue: CmsPrescriptionBlockBlue,
  CmsHomePageCallouts: CmsHomePageCallouts,
  CmsSignUpToday: CmsSignUpToday,
  CmsErrorHero: CmsErrorHero,
  CmsHpsfHeroBanner: CmsHpsfHeroBanner,
  CmsSpecialPricingText: CmsSpecialPricingText,
  CmsShoppingLinksBlue: CmsShoppingLinksBlue,
  CmsNoSearchResultsSuggestions: CmsNoSearchResultsSuggestions,
  'Digital Toolkit': CmsDigitalToolkit,
};

const getComponentName = (componentData: ComponentData): string | null => {
  if ('component' in componentData && typeof componentData.component === 'string') {
    return componentData.component;
  }

  const componentKey = Object.keys(componentData)[0];
  return (componentData as PageComponentItem)[componentKey]?.component || null;
};

const getComponentProps = (componentData: ComponentData): Record<string, unknown> => {
  if ('component' in componentData && typeof componentData.component === 'string') {
    return componentData;
  }

  const componentKey = Object.keys(componentData)[0];
  return (componentData as PageComponentItem)[componentKey] || {};
};

const getComponentUid = (componentData: ComponentData): string => {
  if ('component' in componentData && typeof componentData.component === 'string') {
    return (componentData as FlatComponentItem)._metadata?.uid || componentData.component;
  }

  const componentKey = Object.keys(componentData)[0];
  return (componentData as PageComponentItem)[componentKey]?._metadata?.uid || componentKey;
};

export const CmsComponentRenderer: React.FC<CmsComponentRendererProps> = ({
  components,
  onClose,
  welcomeFlowTracking,
}) => {
  if (!components || components.length === 0) {
    return null;
  }

  return (
    <div className="cms-component-renderer">
      {components.map((componentData) => {
        const componentName = getComponentName(componentData);
        const componentProps = getComponentProps(componentData);
        const componentUid = getComponentUid(componentData);

        if (!componentName) {
          console.warn('[CmsComponentRenderer] No component name found for:', componentData);
          return null;
        }

        const Component = componentMap[componentName];

        if (!Component) {
          console.warn(
            `[CmsComponentRenderer] Component "${componentName}" not found in component map`
          );
          return null;
        }

        return (
          <Component
            key={componentUid}
            {...componentProps}
            data={componentProps}
            onClose={onClose}
            welcomeFlowTracking={welcomeFlowTracking}
          />
        );
      })}
    </div>
  );
};

export default CmsComponentRenderer;
